import React from 'react';
import { Info, FileText, BookOpen, Pencil, CheckSquare, File, Clock, List, Search } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';

const ATSHacks = () => {
  return (
    <DashboardLayout>
      <div className="container px-4 md:px-6 py-8">
      {/* Header */}
      <h1 className="text-4xl font-bold mb-8">Applicant Tracking System (ATS) Hacks</h1>

      {/* Info Box */}
      <div className="bg-gray-100 p-6 rounded-lg mb-12">
        <div className="flex items-start gap-3 mb-4">
          <Info className="w-5 h-5 mt-1" />
          <div>
            <h2 className="font-medium mb-2">Guide Overview</h2>
            <p className="text-gray-700">
              Ensure your resume makes it past the ATS and into the hands of hiring managers with these actionable hacks. 
              Follow this guide to optimize your resume for ATS and increase your chances of landing interviews.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Sections */}
      <h2 className="text-3xl font-bold mb-8">Essential ATS Optimization Steps</h2>

      {/* Section 1 - Formatting */}
      <div className="bg-blue-50 p-6 rounded-lg mb-6">
        <div className="flex items-start gap-4">
          <FileText className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="font-semibold mb-4">1) Standard Formatting</h3>
            <ul className="space-y-4 list-disc">
              <li>Avoid using tables, text boxes, or graphics that ATS systems may not recognize</li>
              <li>Stick to simple fonts like Arial, Times New Roman, or Calibri</li>
              <li>Use a clean and professional layout with clear section headings</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Section 2 - Keywords */}
      <div className="bg-blue-50 p-6 rounded-lg mb-6">
        <div className="flex items-start gap-4">
          <BookOpen className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="font-semibold mb-4">2) Job-Specific Keywords</h3>
            <ul className="space-y-4 list-disc">
              <li>Extract keywords directly from the job description</li>
              <li>Use exact phrases where possible (e.g., "project management" instead of "managing projects")</li>
              <li>Place keywords in the headline, summary, skills section, and work experience</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Section 3 - Symbols and Formatting */}
      <div className="bg-blue-50 p-6 rounded-lg mb-6">
        <div className="flex items-start gap-4">
          <Pencil className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="font-semibold mb-4">3) Avoid Fancy Symbols and Formatting</h3>
            <ul className="space-y-4 list-disc">
              <li>Replace bullet points with standard ones (e.g., • instead of ☑)</li>
              <li>Use plain text for all sections and avoid decorative borders or lines</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Section 4 - File Name */}
      <div className="bg-blue-50 p-6 rounded-lg mb-6">
        <div className="flex items-start gap-4">
          <File className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="font-semibold mb-4">4) Use a Professional File Name</h3>
            <ul className="space-y-4 list-disc">
              <li>Save your resume with a clear and professional file name (Example: "Jane_Doe_Resume.pdf")</li>
              <li>Avoid generic names like "resume.pdf" or "document1.docx"</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Section 5 - File Format */}
      <div className="bg-blue-50 p-6 rounded-lg mb-6">
        <div className="flex items-start gap-4">
          <FileText className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="font-semibold mb-4">5) Submit in the Right File Format</h3>
            <ul className="space-y-4 list-disc">
              <li>Use PDF or Word documents unless the job posting specifies otherwise</li>
              <li>Ensure your PDF is ATS-compatible by checking it with online ATS tools</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Section 6 - Headers and Footers */}
      <div className="bg-blue-50 p-6 rounded-lg mb-6">
        <div className="flex items-start gap-4">
          <BookOpen className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="font-semibold mb-4">6) Avoid Headers and Footers</h3>
            <ul className="space-y-4 list-disc">
              <li>Do not place important information (e.g., contact details) in headers or footers</li>
              <li>ATS may skip or misread content in these areas</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Section 7 - Job Titles */}
      <div className="bg-blue-50 p-6 rounded-lg mb-6">
        <div className="flex items-start gap-4">
          <Clock className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="font-semibold mb-4">7) Keep Job Titles and Dates Simple</h3>
            <ul className="space-y-4 list-disc">
              <li>Clearly list your job titles and dates of employment</li>
              <li>Example format: Marketing Manager | XYZ Company | January 2020 - Present</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Section 8 - Skills */}
      <div className="bg-blue-50 p-6 rounded-lg mb-6">
        <div className="flex items-start gap-4">
          <List className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="font-semibold mb-4">8) Include a Skills Section</h3>
            <ul className="space-y-4 list-disc">
              <li>Create a dedicated "Skills" section with a mix of hard and soft skills</li>
              <li>Prioritize skills mentioned in the job description</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Section 9 - Proofreading */}
      <div className="bg-blue-50 p-6 rounded-lg mb-6">
        <div className="flex items-start gap-4">
          <CheckSquare className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="font-semibold mb-4">9) Proofread for Accuracy</h3>
            <ul className="space-y-4 list-disc">
              <li>Check for spelling, grammar, and formatting errors</li>
              <li>Ensure consistency in punctuation and capitalization</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Section 10 - Testing */}
      <div className="bg-blue-50 p-6 rounded-lg mb-12">
        <div className="flex items-start gap-4">
          <Search className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="font-semibold mb-4">10) Test Your Resume</h3>
            <ul className="space-y-4 list-disc">
              <li>Use online ATS simulators to check your resume's compatibility</li>
              <li>Adjust based on feedback to ensure it parses correctly</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Final Tips Section */}
      <h2 className="text-3xl font-bold mb-6">Final Tips</h2>
      <div className="bg-gray-100 p-6 rounded-lg">
        <div className="flex items-start gap-3 mb-4">
          <CheckSquare className="w-5 h-5 mt-1" />
          <div>
            <p className="mb-4">
              By applying these ATS hacks, you'll significantly improve your chances of getting noticed by employers. 
              Take the time to optimize your resume and leap past the competition!
            </p>
            <p className="font-medium mt-6 mb-4">Remember to:</p>
            <ul className="list-disc pl-6 space-y-3">
              <li>Check for spelling and grammar errors</li>
              <li>Maintain consistency in formatting</li>
              <li>Test your resume with ATS simulation tools</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    </DashboardLayout>
  );
};

export default ATSHacks;